import { useMemo } from "react";

import type { UseTourOptions } from "@frontend/components/organisms/Tour";
import { useLingui } from "@lingui/react/macro";

/**
 * The island renders into its own shadow root, which `querySelector` will not
 * cross: `>>>` names the host to open on the way.
 */
const HOST = "rwc-project-list";

const target = (selector: string) => `${HOST} >>> ${selector}`;

/**
 * What the list is worth pointing at, in the order someone meets it: narrow it,
 * narrow it further, open one, act on one, move on.
 *
 * The same five stops the legacy subscriptions page walks through.
 */
export function useProjectListTour(): UseTourOptions {
  const { t } = useLingui();

  return useMemo(
    () => ({
      storageKey: "project-list-v1",
      steps: [
        {
          icon: "Search",
          target: target('[data-slot="toolbar"] input'),
          side: "bottom",
          title: t`Find a project`,
          content: t`The server searches the name, the client and the identifier. Every project, not the page on screen.`,
        },
        {
          icon: "ListFilter",
          target: target('[data-slot="data-table-filter"]'),
          side: "bottom",
          align: "start",
          interactive: "click",
          title: t`Narrow a column`,
          content: t`Each column filters on its own terms: a text, a number, or a pick from a list. They add up, and the server applies them.`,
        },
        {
          icon: "PanelRight",
          target: target('[data-slot="data-table-row"]'),
          side: "bottom",
          title: t`Read one without leaving`,
          content: t`A row opens a panel beside the list: where the project stands, its contract, its addresses and its people. The arrow keys then walk from one to the next.`,
        },
        {
          icon: "MousePointerClick",
          target: target('[data-slot="data-table-row"]'),
          side: "bottom",
          interactive: "contextmenu",
          title: t`Act on one`,
          content: t`The row menu edits, archives or deletes. The two icons at its end open the last month report and the editor.`,
        },
        {
          icon: "ChevronsLeftRight",
          target: target('[data-slot="pagination"]'),
          side: "top",
          title: t`Move through the pages`,
          content: t`One page is read at a time. The picker beside the search says how many rows it holds.`,
        },
      ],
    }),
    [t],
  );
}
